import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from "@capacitor/core";
import axios from 'axios';
import Api from '../../Api/Api.json';
import { Echo } from "iosplgn";
import {
  VictoryChart,
  VictoryBar,
  VictoryAxis,
  VictoryLabel,
} from 'victory';

// Icons for activity metrics
const StepsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
  </svg>
);

const CaloriesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 2v20M2 12h20" />
    <path d="M12 2a10 10 0 0 1 0 20" />
    <path d="M12 2a10 10 0 0 0 0 20" />
  </svg>
);

const DistanceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <polygon points="10,8 16,12 10,16 10,8"></polygon>
  </svg>
);

const ExerciseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
    <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z"></path>
  </svg>
);

const RecordIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="w-8 h-8"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 11l3 3L22 4"></path>
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
  </svg>
);

const Activity = () => {
  const navigate = useNavigate();
  const [isExerciseActive, setIsExerciseActive] = useState(false);
  const isExerciseActiveRef = useRef(false);
  const [exerciseData, setExerciseData] = useState({
    stepCount: 0,
    calories: 0,
    distance: 0,
    heartRate: 0,
    activeTime: 0,
    temp: 0,
    bloodOxygen: 0
  });

  const [elapsedTime, setElapsedTime] = useState(0);
  const [status, setStatus] = useState('');
  const [showReminder, setShowReminder] = useState(false);
  const intervalRef = useRef(null);
  const timerRef = useRef(null);
  const reminderTimerRef = useRef(null);
  const [isConnected, setConnected] = useState(false);
  const exercise_session_data_array = useRef([]);
  const lastStepDataTimeRef = useRef(null);
  const exerciseDataCheckIntervalRef = useRef(null);
  const initialStepDataRef = useRef(null); // Store initial values when exercise starts
  const lastExerciseDataRef = useRef(null); // Store last data point for display on stop
  const currentHeartRateRef = useRef(0); // Store current heartrate from realtime heartrate listener
  const heartRateIntervalRef = useRef(null); // Interval for requesting heartrate every 30 seconds

  // Keep ref in sync with state for use in listeners
  useEffect(() => {
    isExerciseActiveRef.current = isExerciseActive;
  }, [isExerciseActive]);
  
  // Exercise Record state
  const [patientId, setPatientId] = useState(null);
  const [exerciseList, setExerciseList] = useState([]);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [exerciseError, setExerciseError] = useState(null);

  // Get patient ID from localStorage
  useEffect(() => {
    if (localStorage.getItem('user_info') !== null) {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      setPatientId(userInfo.di_id);
    }
  }, []);

  // Add listener for real-time step data
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !Echo) {
      setStatus("Web platform - device features not available");
      return;
    }

    // Add listener for real-time step data
    Echo.addListener("realtimestep", handleStepData);
    Echo.addListener("realtimeheartrate", handleRealTimeHeartRateData);
    Echo.addListener("connectionResult", handleConnectionEvent); //auto connect event
    Echo.addListener("disconnectionResult", handleDisconnectionEvent); //auto disconnect event
  

    var jstyle_address = localStorage.getItem("jstyle_address");
    if (jstyle_address === null) {
      Echo.checkPermission().then((result) => {
        console.log(result.permission);
        if (result.permission === "granted") {
        } else {
          setStatus("App Requires Bluetooth Permission");
          Echo.requestPermission();
        }
      });
    } else {
      Echo.isConnected().then((result) => {
        if (result.value === "connected") {
          setConnected(true);
        } else {
          Echo.connectDevice({ deviceId: jstyle_address });
        }
      }); 
    }

    return () => {
      if (Echo) {
        Echo.removeListener("realtimestep");
        Echo.removeListener("realtimeheartrate");
        Echo.removeListener("connectionResult");
        Echo.removeListener("disconnectionResult");
        Echo.disconnectDevice();
      }
      // Clean up timers
      if (reminderTimerRef.current) {
        clearTimeout(reminderTimerRef.current);
      }
      if (exerciseDataCheckIntervalRef.current) {
        clearInterval(exerciseDataCheckIntervalRef.current);
      }
      if (heartRateIntervalRef.current) {
        clearInterval(heartRateIntervalRef.current);
      }
    };
  }, []);

  const handleConnectionEvent = (deviceInfo) => {
    if (!Capacitor.isNativePlatform() || !Echo) {
      return;
    }
    setStatus("connected");
    localStorage.setItem("jstyle_address", deviceInfo.peripheralId);
    setConnected(true);
  };
  
  const handleDisconnectionEvent = (deviceInfo) => {
    setStatus("disconnected");
    setConnected(false);
  };

  const handleRealTimeHeartRateData = (heartRateData) => {
    console.log("Real-time heart rate data received:", heartRateData.rtdata.heartRate);
    if (heartRateData.rtdata.heartRate !== 0) {
      currentHeartRateRef.current = parseInt(heartRateData.rtdata.heartRate) || 0;
    }
  };

  // Helper function to format timestamp to "yyyy-mm-dd HH:MM:SS"
  const formatTimestamp = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleStepData = (stepResult) => {
    console.log("Real-time step data received:", stepResult.rtdata);
    
    // Update last step data received timestamp for monitoring
    lastStepDataTimeRef.current = Date.now();
    
    // iOS plugin returns data in rtdata object
    const rtdata = stepResult.rtdata || {};
    
    // Only update exercise data if exercise is active
    if (isExerciseActiveRef.current) {
      // Capture initial values on first data after exercise starts
      if (initialStepDataRef.current === null) {
        initialStepDataRef.current = {
          step: parseInt(rtdata.step) || 0,
          calories: parseInt(rtdata.calories) || 0,
          distance: Math.round(parseFloat(rtdata.distance) * 1000) || 0,
          time: parseInt(rtdata.time) || 0,
          active_time: parseInt(rtdata.StrengthTrainingTime) || 0
        };
        console.log("Initial step data captured:", initialStepDataRef.current);
      }
      
      // Calculate differences from initial values
      const initial = initialStepDataRef.current || {
        step: 0,
        calories: 0,
        distance: 0,
        time: 0,
        active_time: 0
      };
      
      const currentStep = parseInt(rtdata.step) || 0;
      const currentCal = parseInt(rtdata.calories) || 0;
      const currentDistance = Math.round(parseFloat(rtdata.distance) * 1000) || 0;
      const currentTime = parseInt(rtdata.time) || 0;
      const currentActiveTime = parseInt(rtdata.StrengthTrainingTime) || 0;
      
      // Update exercise data with differences from initial values
      // Use heartrate from realtime heartrate listener instead of step data
      const newExerciseData = {
        stepCount: currentStep - initial.step,
        calories: currentCal - initial.calories,
        distance: currentDistance - initial.distance,
        heartRate: currentHeartRateRef.current || 0,
        activeTime: currentActiveTime - initial.active_time,
        temp: parseFloat(rtdata.temperature) || 0,
        bloodOxygen: parseInt(rtdata.spo2) || 0
      };
      
      setExerciseData(newExerciseData);
      
      // Store last data point for display on stop
      lastExerciseDataRef.current = newExerciseData;
    }

    // Add data to exercise_session_data_array if 5 seconds have passed since last addition
    if (isExerciseActiveRef.current) {
      const now = Date.now();
      const lastItem = exercise_session_data_array.current[exercise_session_data_array.current.length - 1];
      
      // Add if array is empty or 5 seconds (5000ms) have passed since last addition
      const lastTimestamp = lastItem 
        ? (typeof lastItem.timestamp === 'string' 
          ? new Date(lastItem.timestamp.replace(' ', 'T')).getTime() 
          : lastItem.timestamp)
        : 0;
      
      if ((!lastItem || (now - lastTimestamp >= 5000)) && currentHeartRateRef.current !== 0) {
        // Calculate differences from initial values
        const initial = initialStepDataRef.current || {
          step: 0,
          calories: 0,
          distance: 0,
          time: 0,
          active_time: 0
        };
        
        const currentStep = parseInt(rtdata.step) || 0;
        const currentCal = parseInt(rtdata.calories) || 0;
        const currentDistance = Math.round(parseFloat(rtdata.distance) * 1000) || 0;
        const currentTime = parseInt(rtdata.time) || 0;
        const currentActiveTime = parseInt(rtdata.StrengthTrainingTime) || 0;
        
        const newDataItem = {
          timestamp: formatTimestamp(now),
          time: currentTime - initial.time,
          steps: currentStep - initial.step,
          calories: currentCal - initial.calories,
          distance: currentDistance - initial.distance,
          heartrate: currentHeartRateRef.current || 0,
          active_time: currentActiveTime - initial.active_time,
          temperature: rtdata.temperature || 0,
          blood_oxygen: rtdata.spo2 || 0
        };
        exercise_session_data_array.current.push(newDataItem);
        console.log("Added exercise data to array:", newDataItem);
      }
    }
  };

  // Function to check if rtStepData listener is triggered every 20 seconds
  const checkExerciseDataReturn = () => {
    // Use ref instead of state to avoid stale closure issue
    if (!isExerciseActiveRef.current) {
      return;
    }

    // Clear any existing interval
    if (exerciseDataCheckIntervalRef.current) {
      clearInterval(exerciseDataCheckIntervalRef.current);
    }

    // Set initial timestamp when check starts
    if (lastStepDataTimeRef.current === null) {
      lastStepDataTimeRef.current = Date.now();
    }

    // Check every 20 seconds
    exerciseDataCheckIntervalRef.current = setInterval(() => {
      // Use ref instead of state to avoid stale closure issue
      if (!isExerciseActiveRef.current) {
        // Stop checking if exercise is no longer active
        if (exerciseDataCheckIntervalRef.current) {
          clearInterval(exerciseDataCheckIntervalRef.current);
          exerciseDataCheckIntervalRef.current = null;
        }
        return;
      }

      const now = Date.now();
      const lastDataTime = lastStepDataTimeRef.current;
      
      if (lastDataTime === null) {
        console.warn("checkExerciseDataReturn: No step data received yet");
        return;
      }

      const timeSinceLastData = now - lastDataTime;
      
      if (timeSinceLastData > 20000) {
        // More than 20 seconds since last data
        console.warn(`checkExerciseDataReturn: No rtStepData received for ${Math.round(timeSinceLastData / 1000)} seconds`);
        Echo.getRealtimeStepData({ intention: "start" });
      } else {
        console.log(`checkExerciseDataReturn: rtStepData is being received (last data ${Math.round(timeSinceLastData / 1000)}s ago)`);
      }
    }, 20000); // Check every 20 seconds
  };

  const handleCardClick = (metricType) => {
    let dataType;
    
    switch(metricType) {
      case 'steps':
        dataType = 'dailysteps';
        break;
      case 'calories':
        dataType = 'dailycalories';
        break;
      case 'distance':
        dataType = 'dailydistance';
        break;
      default:
        return;
    }
    
    // Navigate to VisualizeWellnessData with data type
    navigate('/landing/tab3/VisualizeWellnessData', {
      state: {
        dataType: dataType
      }
    });
  };

  const startExercise = () => {
    if (!Capacitor.isNativePlatform() || !Echo) {
      setStatus("Device exercise tracking not available on web platform");
      return;
    }

    setIsExerciseActive(true);
    isExerciseActiveRef.current = true; // Update ref immediately for listeners
    setElapsedTime(0);
    setStatus("Starting exercise tracking...");
    setShowReminder(false);

    // Reset exercise data
    setExerciseData({
      stepCount: 0,
      calories: 0,
      distance: 0,
      heartRate: 0,
      activeTime: 0,
      temp: 0,
      bloodOxygen: 0
    });

    // Reset exercise session data array
    exercise_session_data_array.current = [];
    
    // Reset initial step data ref to capture first data after start
    initialStepDataRef.current = null;
    lastExerciseDataRef.current = null;
    lastStepDataTimeRef.current = null;
    currentHeartRateRef.current = 0; // Reset heartrate

    // Call iOS plugin to start real-time step tracking
    Echo.getRealtimeStepData({ intention: "start" })
      .then((result) => {
        console.log("Exercise tracking started:", result);
        setStatus("Exercise tracking active");
        
        // Start timer for elapsed time
        timerRef.current = setInterval(() => {
          setElapsedTime(prev => {
            const newTime = prev + 1;
            // Auto-stop at 30 minutes (1800 seconds)
            if (newTime >= 1800) {
              stopExercise();
              return 1800;
            }
            return newTime;
          });
        }, 1000);

        // Set 30-minute reminder
        reminderTimerRef.current = setTimeout(() => {
          setShowReminder(true);
          setStatus("30 minutes completed - check if you want to continue");
        }, 30 * 60 * 1000); // 30 minutes

        // Check if rtStepData listener is triggered every 20 seconds
        checkExerciseDataReturn();

        // Start requesting heartrate every 30 seconds
        // Request immediately on start
        Echo.getRealtimeMeasurementData({ measurementType: "heartrate" })
          .then((result) => {
            console.log("Heartrate measurement started:", result);
          })
          .catch((error) => {
            console.error("Error starting heartrate measurement:", error);
          });

        // Set interval to request heartrate every 30 seconds
        heartRateIntervalRef.current = setInterval(() => {
          if (isExerciseActiveRef.current) {
            Echo.getRealtimeMeasurementData({ measurementType: "heartrate" })
              .then((result) => {
                console.log("Heartrate measurement requested:", result);
              })
              .catch((error) => {
                console.error("Error requesting heartrate measurement:", error);
              });
          }
        }, 30000); // 30 seconds
      })
      .catch((error) => {
        console.error("Error starting exercise tracking:", error);
        setStatus("Failed to start exercise tracking");
        setIsExerciseActive(false);
        isExerciseActiveRef.current = false; // Update ref immediately for listeners
        
        // Stop exercise data check if exercise failed to start
        if (exerciseDataCheckIntervalRef.current) {
          clearInterval(exerciseDataCheckIntervalRef.current);
          exerciseDataCheckIntervalRef.current = null;
        }
        if (heartRateIntervalRef.current) {
          clearInterval(heartRateIntervalRef.current);
          heartRateIntervalRef.current = null;
        }
        lastStepDataTimeRef.current = null;
        initialStepDataRef.current = null; // Reset initial data
        currentHeartRateRef.current = 0; // Reset heartrate
      });
  };

  const stopExercise = () => {
    if (!Capacitor.isNativePlatform() || !Echo) {
      setIsExerciseActive(false);
      isExerciseActiveRef.current = false; // Update ref immediately for listeners
      
      // Stop exercise data check
      if (exerciseDataCheckIntervalRef.current) {
        clearInterval(exerciseDataCheckIntervalRef.current);
        exerciseDataCheckIntervalRef.current = null;
      }
      if (heartRateIntervalRef.current) {
        clearInterval(heartRateIntervalRef.current);
        heartRateIntervalRef.current = null;
      }
      lastStepDataTimeRef.current = null;
      initialStepDataRef.current = null; // Reset initial data
      currentHeartRateRef.current = 0; // Reset heartrate
      return;
    }

    setStatus("Stopping exercise tracking...");
    
    // Call iOS plugin to stop real-time step tracking
    Echo.getRealtimeStepData({ intention: "stop" })
      .then((result) => {
        console.log("Exercise tracking stopped:", result);
        setStatus("Exercise tracking stopped");
        setIsExerciseActive(false);
        isExerciseActiveRef.current = false; // Update ref immediately for listeners
        setShowReminder(false);
        
        // Show last data point (relative to initial) instead of new data
        if (lastExerciseDataRef.current) {
          setExerciseData(lastExerciseDataRef.current);
        }
        
        // Clear timers
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        if (reminderTimerRef.current) {
          clearTimeout(reminderTimerRef.current);
          reminderTimerRef.current = null;
        }

        // Stop exercise data check
        if (exerciseDataCheckIntervalRef.current) {
          clearInterval(exerciseDataCheckIntervalRef.current);
          exerciseDataCheckIntervalRef.current = null;
        }
        if (heartRateIntervalRef.current) {
          clearInterval(heartRateIntervalRef.current);
          heartRateIntervalRef.current = null;
        }
        lastStepDataTimeRef.current = null;
        initialStepDataRef.current = null; // Reset initial data
        currentHeartRateRef.current = 0; // Reset heartrate

        // Upload exercise session data
        const uploadTimestamp = formatTimestamp(Date.now()); // Format as "yyyy-mm-dd HH:MM:SS"
        const patientId = localStorage.getItem('user_info') 
          ? JSON.parse(localStorage.getItem('user_info')).di_id 
          : null;

        if (patientId && exercise_session_data_array.current.length > 0) {
          const requestData = {
            task: "upload_exercise_session_data",
            patient_id: patientId,
            timestamp: uploadTimestamp,
            exercise_session_data_array: exercise_session_data_array.current
          };

          const headers = { headers: { "Content-Type": "application/json" } };
          const url = Api.EXERCISE_SESSION_DATA_API;

          axios.post(url, requestData, headers)
            .then((response) => {
              console.log("Exercise session data uploaded successfully:", JSON.stringify(response));
              setStatus("Exercise data uploaded successfully");
              // Clear array to release memory after successful upload
              exercise_session_data_array.current = [];
              // Refresh exercise list
              if (patientId) {
                fetchExerciseList();
              }
            })
            .catch((error) => {
              console.error("Error uploading exercise session data:", error);
              setStatus("Exercise stopped but upload failed");
              // Clear array to release memory even if upload failed
              exercise_session_data_array.current = [];
            });
        } else {
          if (!patientId) {
            console.warn("Patient ID not found, skipping upload");
          }
          if (exercise_session_data_array.current.length === 0) {
            console.warn("No exercise data to upload");
          }
        }
      })
      .catch((error) => {
        console.error("Error stopping exercise tracking:", error);
        setStatus("Error stopping exercise tracking");
        setIsExerciseActive(false);
        isExerciseActiveRef.current = false; // Update ref immediately for listeners
        setShowReminder(false);
        
        // Clear timers even if there's an error
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        if (reminderTimerRef.current) {
          clearTimeout(reminderTimerRef.current);
          reminderTimerRef.current = null;
        }
        
        if (heartRateIntervalRef.current) {
          clearInterval(heartRateIntervalRef.current);
          heartRateIntervalRef.current = null;
        }
        
        initialStepDataRef.current = null; // Reset initial data
        currentHeartRateRef.current = 0; // Reset heartrate

        // Still try to upload data even if stopping failed
        const uploadTimestamp = formatTimestamp(Date.now()); // Format as "yyyy-mm-dd HH:MM:SS"
        const patientId = localStorage.getItem('user_info') 
          ? JSON.parse(localStorage.getItem('user_info')).di_id 
          : null;

        if (patientId && exercise_session_data_array.current.length > 0) {
          const requestData = {
            task: "upload_exercise_session_data",
            patient_id: patientId,
            timestamp: uploadTimestamp,
            exercise_session_data_array: exercise_session_data_array.current
          };

          const headers = { headers: { "Content-Type": "application/json" } };
          const url = Api.EXERCISE_SESSION_DATA_API;

          axios.post(url, requestData, headers)
            .then((response) => {
              console.log("Exercise session data uploaded successfully:", response);
              // Clear array to release memory after successful upload
              exercise_session_data_array.current = [];
            })
            .catch((error) => {
              console.error("Error uploading exercise session data:", error);
              // Clear array to release memory even if upload failed
              exercise_session_data_array.current = [];
            });
        }
      });
  };

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (reminderTimerRef.current) {
        clearTimeout(reminderTimerRef.current);
      }
      if (exerciseDataCheckIntervalRef.current) {
        clearInterval(exerciseDataCheckIntervalRef.current);
      }
      if (heartRateIntervalRef.current) {
        clearInterval(heartRateIntervalRef.current);
      }
    };
  }, []);

  const handleContinueExercise = () => {
    setShowReminder(false);
    setStatus("Exercise tracking continued");
    
    // Set another 30-minute reminder
    reminderTimerRef.current = setTimeout(() => {
      setShowReminder(true);
      setStatus("30 minutes completed - check if you want to continue");
    }, 30 * 60 * 1000); // 30 minutes
  };

  // Auto-stop if user doesn't respond to reminder within 1 minute
  useEffect(() => {
    if (showReminder) {
      const autoStopTimer = setTimeout(() => {
        console.log("Auto-stopping exercise due to no response to reminder");
        stopExercise();
      }, 60 * 1000); // 1 minute timeout

      return () => clearTimeout(autoStopTimer);
    }
  }, [showReminder]);

  const handleStopExercise = () => {
    stopExercise();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch exercise session list
  const fetchExerciseList = async () => {
    if (!patientId) return;
    
    setIsLoadingList(true);
    setExerciseError(null);
    
    try {
      const headers = { headers: { "Content-Type": "application/json" } };
      const url = Api.EXERCISE_SESSION_DATA_API;
      const requestData = {
        task: "get_exercise_session_list",
        patient_id: patientId
      };
      
      const response = await axios.post(url, requestData, headers);
      
      // Handle different response structures
      let responseData = response.data;
      if (responseData?.body) responseData = responseData.body;
      if (responseData?.data) responseData = responseData.data;
      
      if (responseData && Array.isArray(responseData)) {
        // Map API response to expected format
        const mappedData = responseData.map(item => {
          // Parse timestamp - API might return it as string or timestamp
          let timestamp;
          if (item.timestamp) {
            timestamp = typeof item.timestamp === 'string' 
              ? new Date(item.timestamp) 
              : new Date(item.timestamp * 1000); // if it's Unix timestamp
          } else if (item.datetime) {
            timestamp = new Date(item.datetime);
          } else {
            timestamp = new Date();
          }
          
          return {
            datetime: item.datetime || timestamp.toISOString(),
            timestamp: timestamp,
            // Store original timestamp for API call
            originalTimestamp: item.timestamp || item.datetime
          };
        });
        
        // Sort by datetime descending (newest first)
        const sortedData = mappedData.sort((a, b) => b.timestamp - a.timestamp);
        setExerciseList(sortedData);
        
        // Set first item as selected by default
        if (sortedData.length > 0) {
          setSelectedExercise(sortedData[0]);
        }
      } else {
        setExerciseList([]);
      }
    } catch (err) {
      console.error('Error fetching exercise session list:', err);
      setExerciseError('Failed to load exercise sessions. Please try again.');
      setExerciseList([]);
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    fetchExerciseList();
  }, [patientId]);
  
  // Fetch exercise session details when selected
  useEffect(() => {
    const fetchExerciseDetails = async () => {
      if (!patientId || !selectedExercise || !selectedExercise.originalTimestamp) return;
      
      setIsLoadingDetails(true);
      setExerciseError(null);
      
      try {
        const headers = { headers: { "Content-Type": "application/json" } };
        const url = Api.EXERCISE_SESSION_DATA_API;
        
        // Use original timestamp string as-is without timezone transformation
        const timestampForApi = typeof selectedExercise.originalTimestamp === 'string' 
          ? selectedExercise.originalTimestamp
          : selectedExercise.originalTimestamp instanceof Date
            ? selectedExercise.originalTimestamp.toISOString()
            : String(selectedExercise.originalTimestamp);
        
        const requestData = {
          task: "get_exercise_session_data",
          patient_id: patientId,
          timestamp: timestampForApi
        };
        
        const response = await axios.post(url, requestData, headers);
        
        // Handle different response structures
        let responseData = response.data;
        if (responseData?.body) responseData = responseData.body;
        if (responseData?.data) responseData = responseData.data;
        
        // Handle array response - take first item if array
        if (Array.isArray(responseData) && responseData.length > 0) {
          responseData = responseData[0];
        }
        
        if (responseData) {
          // Parse zone_summary and zone_map if they are JSON strings
          let parsedZoneSummary = null;
          let parsedZoneMap = null;
          
          if (responseData.zone_summary) {
            if (typeof responseData.zone_summary === 'string') {
              try {
                parsedZoneSummary = JSON.parse(responseData.zone_summary);
              } catch (e) {
                console.error('Error parsing zone_summary JSON:', e);
                parsedZoneSummary = null;
              }
            } else {
              parsedZoneSummary = responseData.zone_summary;
            }
          }
          
          if (responseData.zone_map) {
            if (typeof responseData.zone_map === 'string') {
              try {
                parsedZoneMap = JSON.parse(responseData.zone_map);
              } catch (e) {
                console.error('Error parsing zone_map JSON:', e);
                parsedZoneMap = null;
              }
            } else {
              parsedZoneMap = responseData.zone_map;
            }
          }
          
          // Merge API response with existing exercise data
          const updatedExercise = {
            ...selectedExercise,
            heartrate_min: responseData.heartrate_min ?? selectedExercise.heartrate_min,
            heartrate_max: responseData.heartrate_max ?? selectedExercise.heartrate_max,
            heartrate_avg: responseData.heartrate_avg ?? selectedExercise.heartrate_avg,
            total_time: responseData.total_time ?? selectedExercise.total_time,
            total_steps: responseData.total_steps ?? selectedExercise.total_steps,
            total_distance: responseData.total_distance ?? selectedExercise.total_distance,
            total_calories: responseData.total_calories ?? selectedExercise.total_calories,
            avg_speed: responseData.avg_speed ?? selectedExercise.avg_speed,
            vo2max: responseData.vo2max ?? selectedExercise.vo2max,
            cardio_recovery_score: responseData.cardio_recovery_score ?? responseData.cardio_recovery ?? selectedExercise.cardio_recovery_score,
            // Store parsed zone_summary and zone_map from API response
            zone_summary: parsedZoneSummary,
            zone_map: parsedZoneMap
          };
          
          setSelectedExercise(updatedExercise);
        }
      } catch (err) {
        console.error('Error fetching exercise session details:', err);
        setExerciseError('Failed to load exercise session details. Please try again.');
      } finally {
        setIsLoadingDetails(false);
      }
    };
    
    fetchExerciseDetails();
  }, [patientId, selectedExercise?.originalTimestamp]);

  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    setIsDropdownOpen(false);
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const formatDistance = (km) => {
    if (!km) return '0 m';
    // No need to convert meters. Api returns meters directly.
    const meters = Math.round(km * 1);
    return `${meters} m`;
  };

  const formatSpeed = (kmh) => {
    if (!kmh) return '0 km/h';
    return `${kmh.toFixed(1)} km/h`;
  };

  // Process zone_map data for chart
  const zoneMapChartData = useMemo(() => {
    if (!selectedExercise || !selectedExercise.zone_map || !Array.isArray(selectedExercise.zone_map)) {
      return [];
    }

    const chartData = [];

    selectedExercise.zone_map.forEach((entry, index) => {
      // Handle zone = -1 (no data)
      const zone = entry.zone === -1 ? -1 : (entry.zone >= 0 && entry.zone <= 5 ? entry.zone : 0);
      
      // Each bar represents 30 seconds, so x position is in minutes
      const minutes = (index * 30 + 15) / 60; // Center of each 30-second bin
      
      // Get avg_heartrate for label
      const avgHeartrate = entry.avg_heartrate || null;

      chartData.push({
        x: minutes,
        y: zone,
        avgHeartrate: avgHeartrate
      });
    });

    return chartData;
  }, [selectedExercise]);

  // Process zone_summary data for chart
  const zoneSummaryChartData = useMemo(() => {
    if (!selectedExercise || !selectedExercise.zone_summary || typeof selectedExercise.zone_summary !== 'object') {
      return [];
    }

    // zone_summary format: {"0": 5, "1": 14, "2": 0.5, "-1": 10, ...}
    // Ignore zone=-1, only process zones 0-5
    const zoneCounts = [0, 0, 0, 0, 0, 0]; // Zones 0-5
    
    Object.keys(selectedExercise.zone_summary).forEach(zoneKey => {
      const zone = parseInt(zoneKey);
      const minutes = selectedExercise.zone_summary[zoneKey];
      // Ignore zone=-1 and only process valid zones 0-5
      if (zone >= 0 && zone <= 5 && typeof minutes === 'number') {
        zoneCounts[zone] = minutes;
      }
    });

    // Convert to chart data format
    return zoneCounts.map((minutes, zone) => ({
      x: zone,
      y: minutes
    }));
  }, [selectedExercise]);

  const activityMetrics = [
    {
      id: 'steps',
      name: 'Steps',
      bengaliName: 'Steps',
      icon: <StepsIcon />,
      description: 'Track your daily step count and walking activity',
      color: 'bg-[#58A6FF]',
      unit: 'steps',
      currentValue: '8,432'
    },
    {
      id: 'calories',
      name: 'Calories',
      bengaliName: 'Calories',
      icon: <CaloriesIcon />,
      description: 'Monitor your calorie burn throughout the day',
      color: 'bg-[#58A6FF]',
      unit: 'cal',
      currentValue: '2,156'
    },
    {
      id: 'distance',
      name: 'Distance',
      bengaliName: 'Distance',
      icon: <DistanceIcon />,
      description: 'Track the distance you cover during activities',
      color: 'bg-[#58A6FF]',
      unit: 'm',
      currentValue: '6800'
    }
  ];

  return (
    <div className="min-h-screen pt-14 pb-24 safe-area-top">
      {/* Black background */}
      <div className="absolute inset-0 bg-black"></div>
      
      <div className="container mx-auto px-4 py-6 relative z-10">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white hover:text-blue-400 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium text-white">Back</span>
          </button>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Physical Activity</h1>
          <p className="text-white">Track your daily physical activities</p>
        </div>

        {/* Platform Warning for Web */}
        {!Capacitor.isNativePlatform() && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className="text-yellow-800 font-medium">Web Platform</span>
            </div>
            <p className="text-yellow-700 text-sm mt-1">
              Device exercise tracking features are not available on web platform. Use the mobile app for full functionality.
            </p>
          </div>
        )}

        {/* Exercise Card */}
        <div className="mb-6">
          <div className="bg-[#58A6FF] rounded-lg shadow-lg overflow-hidden">
            {/* Exercise Header */}
            <div className="p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                    <ExerciseIcon />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Exercise</h3>
                    <p className="text-sm opacity-90">Exercise</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">{formatTime(elapsedTime)}</div>
                  <div className="text-xs opacity-90">Time</div>
                </div>
              </div>
            </div>

            {/* Exercise Content */}
            <div className="p-6 bg-[#161B22] border border-[#30363D]">
              <p className="text-white text-sm leading-relaxed mb-4">
                Start your workout session and track real-time metrics
              </p>
              
              {/* Real-time Metrics */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-black rounded-lg">
                  <div className="text-lg font-bold text-white">{exerciseData.stepCount}</div>
                  <div className="text-xs text-white">Steps</div>
                </div>
                <div className="text-center p-3 bg-black rounded-lg">
                  <div className="text-lg font-bold text-white">{exerciseData.calories} kCal</div>
                  <div className="text-xs text-white">Calories</div>
                </div>
                <div className="text-center p-3 bg-black rounded-lg">
                  <div className="text-lg font-bold text-white">{exerciseData.distance} m</div>
                  <div className="text-xs text-white">Distance</div>
                </div>
                <div className="text-center p-3 bg-black rounded-lg">
                  <div className="text-lg font-bold text-white">{exerciseData.heartRate} bpm</div>
                  <div className="text-xs text-white">Heart Rate</div>
                </div>
              </div>

              {/* Status Display */}
              {status && (
                <div className="mb-4 p-3 bg-black rounded-lg">
                  <p className="text-white text-sm font-medium">{status}</p>
                </div>
              )}

              {/* Start/Stop Button */}
              <button
                onClick={isExerciseActive ? stopExercise : startExercise}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors duration-200 ${
                  isExerciseActive
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isExerciseActive ? 'Stop Exercise' : 'Start Exercise'}
              </button>
            </div>
          </div>
        </div>

        {/* Exercise Record Section */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-lg overflow-hidden">
            {/* Exercise Record Header */}
            <div className="p-6 text-white">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <RecordIcon />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">Exercise Record</h3>
                  <p className="text-sm opacity-90">View your exercise history</p>
                </div>
              </div>
            </div>

            {/* Exercise Record Content */}
            <div className="p-6 bg-[#161B22] border border-[#30363D]">
            {/* Error State */}
            {exerciseError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-red-800">{exerciseError}</span>
                </div>
              </div>
            )}

            {/* Exercise List Dropdown */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-white mb-4">Exercise Sessions</h4>
              {isLoadingList ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-white">Loading exercise sessions...</span>
                </div>
              ) : exerciseList.length > 0 ? (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-4 py-3 text-left !bg-[#161B22] border border-[#30363D] rounded-md hover:!bg-[#21262D] focus:outline-none focus:ring-2 focus:ring-[#58A6FF] focus:border-[#58A6FF] transition-colors flex items-center justify-between"
                    style={{ backgroundColor: '#161B22' }}
                  >
                    <span className="text-sm text-white">
                      {selectedExercise 
                        ? formatDateTime(selectedExercise.timestamp)
                        : 'Select an exercise session'
                      }
                    </span>
                    <svg
                      className={`w-5 h-5 text-white transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  
                  {isDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsDropdownOpen(false)}
                      ></div>
                      <div className="absolute z-20 mt-1 w-full bg-[#161B22] border border-[#30363D] rounded-md shadow-lg max-h-60 overflow-auto">
                        {exerciseList.map((exercise, index) => (
                          <button
                            key={index}
                            onClick={() => handleExerciseSelect(exercise)}
                            className={`w-full px-4 py-3 text-left text-sm hover:bg-[#21262D] transition-colors ${
                              selectedExercise?.originalTimestamp === exercise.originalTimestamp
                                ? 'bg-[#21262D] text-[#58A6FF]'
                                : 'text-white'
                            }`}
                          >
                            {formatDateTime(exercise.timestamp)}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : patientId ? (
                <p className="text-white">No exercise data available</p>
              ) : (
                <p className="text-white">No patient data available</p>
              )}
            </div>

            {/* Exercise Details Card */}
            {selectedExercise && (
              <div className="bg-[#161B22] rounded-lg shadow-md p-6 mb-6 border border-[#30363D]">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-semibold text-white">Exercise Details</h4>
                  {isLoadingDetails && (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-sm text-white">Loading...</span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Timestamp</div>
                    <div className="text-sm font-medium text-white">
                      {formatDateTime(selectedExercise.timestamp)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Heart Rate Min</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.heartrate_min} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Heart Rate Max</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.heartrate_max} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Heart Rate Avg</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.heartrate_avg} bpm
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Total Time</div>
                    <div className="text-sm font-medium text-white">
                      {formatDuration(selectedExercise.total_time)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Total Steps</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.total_steps?.toLocaleString() || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Total Distance</div>
                    <div className="text-sm font-medium text-white">
                      {formatDistance(selectedExercise.total_distance)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Total Calories</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.total_calories || 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Avg Speed</div>
                    <div className="text-sm font-medium text-white">
                      {formatSpeed(selectedExercise.avg_speed)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">VO2 Max</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.vo2max} ml/kg/min
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white mb-1 font-bold">Cardio Recovery Score</div>
                    <div className="text-sm font-medium text-white">
                      {selectedExercise.cardio_recovery_score} bpm
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Heart Rate Zone Map Chart */}
        <div className="w-full mt-4 mb-0" style={{ minHeight: '390px', paddingLeft: '5px', paddingRight: '5px' }}>
          <h4 className="text-lg font-semibold text-white mb-4">Heart Rate Zone with Time (Minutes)</h4>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <VictoryChart
            domainPadding={{ x: [10, 10] }}
            padding={{ top: 40, bottom: 80, left: 60, right: 20 }}
            width={zoneMapChartData && zoneMapChartData.length > 0 
              ? Math.max(600, Math.max(zoneMapChartData.length * 40, 400))
              : 600}
            height={390}
            domain={{
              x: zoneMapChartData && zoneMapChartData.length > 0 
                ? [0, (zoneMapChartData.length * 30) / 60]
                : [0, 1],
              y: [-1, 5]
            }}
            style={{ parent: { width: '100%', background: 'transparent' } }}
          >
                    
                    <VictoryAxis
                      style={{
                        axis: { stroke: '#8B949E' },
                        tickLabels: { fontSize: 40, fill: '#F0F6FC', angle: -45, textAnchor: 'end' },
                        grid: null
                      }}
                      tickFormat={(t) => {
                        if (t % 1 === 0) {
                          return `${t}m`;
                        }
                        const mins = Math.floor(t);
                        const secs = Math.round((t - mins) * 60);
                        if (mins === 0) {
                          return `${secs}s`;
                        }
                        return `${mins}m ${secs}s`;
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      /*label="Zone"*/
                      tickValues={[-1, 0, 1, 2, 3, 4, 5]}
                      tickFormat={(t) => t === -1 ? 'ND' : `${t}`}
                      domain={[-1, 5]}
                      style={{
                        axis: { stroke: '#8B949E' },
                        axisLabel: { padding: 45, fontSize: 48, fill: '#F0F6FC' },
                        tickLabels: { fontSize: 30, fill: '#F0F6FC' },
                        grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' }
                      }}
                    />
                    <VictoryBar
                      data={zoneMapChartData || []}
                      style={{
                        data: { 
                          fill: '#58A6FF',
                          stroke: '#58A6FF',
                          strokeWidth: 0.5
                        }
                      }}
                      barWidth={30}
                    />
            </VictoryChart>
          </div>
        </div>

        {/* Zone Summary Chart */}
        <div className="w-full mt-0 mb-6" style={{ minHeight: '300px', paddingLeft: '5px', paddingRight: '5px' }}>
          <h4 className="text-lg font-semibold text-white mb-4">Time Spent in Each Zone</h4>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <VictoryChart
            domainPadding={{ x: 20 }}
            padding={{ top: 0, bottom: 60, left: 60, right: 20 }}
            width={600}
            height={300}
            domain={{ x: [-0.2, 5.5] }}
            style={{ parent: { width: '100%', background: 'transparent' } }}
          >
                    <VictoryAxis
                      /*label="Zone"*/
                      tickValues={[0, 1, 2, 3, 4, 5]}
                      tickFormat={(t) => `${t}`}
                      style={{
                        axis: { stroke: '#8B949E' },
                        axisLabel: { padding: 35, fontSize: 48, fill: '#F0F6FC' },
                        tickLabels: { fontSize: 40, fill: '#F0F6FC' },
                        grid: null
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      /*label="Time (minutes)"*/
                      style={{
                        axis: { stroke: '#8B949E' },
                        axisLabel: { padding: 45, fontSize: 48, fill: '#F0F6FC' },
                        tickLabels: { fontSize: 0, fill: 'transparent' },
                        grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' }
                      }}
                    />
                    <VictoryBar
                      data={zoneSummaryChartData || []}
                      labels={({ datum }) => datum.y > 0 ? `${datum.y} min` : ''}
                      labelComponent={
                        <VictoryLabel
                          dy={-5}
                          style={{ fontSize: 22, fill: '#F0F6FC', fontWeight: 'bold' }}
                        />
                      }
                      style={{
                        data: { 
                          fill: '#58A6FF',
                          stroke: '#58A6FF',
                          strokeWidth: 1
                        },
                        labels: { fontSize: 22, fill: '#F0F6FC', fontWeight: 'bold' }
                      }}
                      barWidth={50}
                    />
            </VictoryChart>
          </div>
        </div>

        {/* Activity Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activityMetrics.map((metric, index) => (
            <div
              key={metric.id}
              onClick={() => handleCardClick(metric.id)}
              className={`bg-[#161B22] rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden group border border-[#30363D] ${
                index === activityMetrics.length - 1 ? 'mb-20' : ''
              }`}
            >
              {/* Metric Header */}
              <div className={`${metric.color} p-6 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      {metric.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold">{metric.name}</h3>
                      <p className="text-sm opacity-90">{metric.bengaliName}</p>
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>

              {/* Metric Content */}
              <div className="p-6">
                <p className="text-white text-sm leading-relaxed mb-4">
                  {metric.description}
                </p>
                
                {/* Current Value */}
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-white">{metric.currentValue}</span>
                    <span className="text-white ml-1">{metric.unit}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">Current Value</div>
                    <div className="text-xs text-green-600">+5.2%</div>
                  </div>
                </div>
                
                {/* Quick Stats */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-white">Average</span>
                      <div className="font-medium text-white">{metric.currentValue} {metric.unit}</div>
                    </div>
                    <div>
                      <span className="text-white">Target</span>
                      <div className="font-medium text-white">
                        {metric.id === 'steps' ? '10,000' : 
                         metric.id === 'calories' ? '2,500' : '8.0'} {metric.unit}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>



        {/* 30-Minute Reminder Dialog */}
        {showReminder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#161B22] rounded-lg shadow-xl max-w-md w-full p-6 border border-[#30363D]">
              <div className="text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto">
                    <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  30 Minutes Completed
                </h3>
                <p className="text-white text-sm mb-6">
                  You've been exercising for 30 minutes. Would you like to continue tracking your exercise?
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleContinueExercise}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Continue
                  </button>
                  <button
                    onClick={handleStopExercise}
                    className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Stop
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Activity;